import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import {
  Attachments,
  FiveWhysQuestionAnswerQuestionEnum,
  GetAllInvestigationQuestionOfOrganizationWtihCategoryResponse,
  GetIncidentByIdResponse,
  GetInvestigationByIdResponse,
  InvestigationQuestionTypeEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import { JSX, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { getInvestigationReportingSchema } from './schema';
import InvestigationReportingStepOne from '../../views/InvestigationReportingStepOne';
import InvestigationReportingStepTwo from '../../views/InvestigationReportingStepTwo';
import InvestigationReportingStepThree from '../../views/InvestigationReportingStepThree';
import InvestigationReportingStepFour from '../../views/InvestigationReportingStepFour';
import InvestigationReportingStepFive from '../../views/InvestigationReportingStepFive';
import { toast } from 'react-toastify';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

export interface InvestigationFormValues {
  injuryIllness: string;
  bodyPartAffected: string[];
  typeOfLoss:
    | InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum
    | string;
  classification: string;
  classifyIllness?: string;

  daysAwayFromWork?: number | null;
  jobRestrictionDays?: number | null;

  files?: File[];

  // Step 2
  fiveWhysQuestionAnswer?: {
    question: string;
    answer: string;
  }[];

  // Step 3
  customQuestions?: {
    questionId: string;
    answer: string;
  }[];

  // Step 4
  rootCauseAnalysis?: string;
  otherQuestions?: {
    questionType: InvestigationQuestionTypeEnum | string;
    question: string;
    answer: string;
    options?: string[];
  }[];
}

export default function InvestigationReportingPage() {
  const {
    IncidentReportingApi,
    InvestigationQuestionApi,
    InvestigationReportingApi,
  } = useApiClients();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [searchParams] = useSearchParams();
  const params = useParams();
  const id = params['id'] || '';
  const investigationId = searchParams.get('investigationId') ?? '';
  const [attachments, setAttachments] = useState<Attachments[]>([]);

  const { data = undefined, isLoading } = useQuery<
    GetIncidentByIdResponse['incident']
  >({
    queryKey: ['get-incident-by-id', id],
    queryFn: async () => {
      const response =
        await IncidentReportingApi.incidentReportingControllerFindIncidentById({
          id,
        });
      return response?.data?.incident;
    },
  });
  const {
    data: investigationQuestions,
    isLoading: isInvestigationQuestionsLoading,
  } = useQuery<
    GetAllInvestigationQuestionOfOrganizationWtihCategoryResponse['investigationQuestions'],
    Error,
    any
  >({
    queryKey: ['investigation-questions', id],
    queryFn: async () => {
      const response =
        await InvestigationQuestionApi?.investigationQuestionControllerGetInvestigationQuestionByCategory();
      return response?.data?.investigationQuestions;
    },
  });

  const { data: investigationData, isLoading: isInvestigationLoading } =
    useQuery<GetInvestigationByIdResponse['investigation']>({
      queryKey: ['get-investigation-by-id-for-edit', investigationId],
      queryFn: async () => {
        const response =
          await InvestigationReportingApi.investigationReportingControllerGetInvestigationById(
            {
              investigationId: investigationId,
            },
          );
        return response?.data?.investigation;
      },
      enabled: !!investigationId,
    });

  const allQuestions = investigationQuestions?.flatMap(
    (category: any) => category.questions,
  );

  const updatedCustomQuestions: any = () => {
    const weHaveAnswerFor =
      investigationData?.customQuestions?.map((innerNode: any) => ({
        id: innerNode?.investigationQuestion?.id,
        question: innerNode?.investigationQuestion?.question,
        answer: innerNode?.answer,
        questionCategory:
          innerNode?.investigationQuestion?.investigationQuestionCategory?.name,
        type: innerNode?.investigationQuestion?.type,
        questionCategoryId:
          innerNode?.investigationQuestion?.investigationQuestionCategory?.id,
        options: innerNode?.investigationQuestion?.options,
      })) || [];
    const filterAllCategories = weHaveAnswerFor?.reduce(
      (acc: any[], curr: any) => {
        if (
          !acc.some(
            (item) => item.questionCategoryId === curr.questionCategoryId,
          )
        ) {
          acc.push(curr);
        }
        return acc;
      },
      [],
    );

    const finalStructure = filterAllCategories?.map((node) => ({
      category: node.questionCategory,
      questions: weHaveAnswerFor?.filter(
        (item) => item.questionCategoryId === node.questionCategoryId,
      ),
    }));

    return finalStructure;
  };
  const finalCustomQuestions = updatedCustomQuestions();

  const INITIAL_VALUES = {
    injuryIllness: '',
    bodyPartAffected: [],
    typeOfLoss: '',
    classification: '',
    classifyIllness: '',
    daysAwayFromWork: null,
    jobRestrictionDays: '',
    dateOfDeath: '',
    files: [],
    fiveWhysQuestionAnswer: Object.values(
      FiveWhysQuestionAnswerQuestionEnum,
    ).map((question) => ({
      question,
      answer: '',
    })),
    narBeforeIncident: '',
    narWhatHappened: '',
    narObjectSubstance: '',
    treatmentFacilityType: '',
    customQuestions:
      allQuestions?.map((q: any) => ({
        questionId: q?.id,
        answer: '',
      })) || [],
    rootCauseAnalysis: '',
    otherQuestions: [],
  };

  const investigationReportingHandler = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: investigationId ? false : true,
    validationSchema: getInvestigationReportingSchema(currentStep, attachments),
    onSubmit: async (values) => {
      try {
        let cleanedData;
        if (investigationId) {
          const flatMap = finalCustomQuestions?.flatMap(
            (category: any) => category.questions,
          );

          if (values?.files?.length > 0 || attachments?.length > 0) {
            cleanedData = {
              type:
                values?.injuryIllness === investigationData?.type
                  ? null
                  : values?.injuryIllness,

              affectedBodyParts: isEqual(
                values?.bodyPartAffected,
                investigationData?.affectedBodyParts,
              )
                ? null
                : values?.bodyPartAffected,

              typeOfLoss:
                values?.typeOfLoss === investigationData?.typeOfLoss
                  ? null
                  : values?.typeOfLoss,

              classifyIllness:
                values?.classifyIllness === investigationData?.classifyIllness
                  ? null
                  : values?.classifyIllness,

              daysAwayFromWork:
                Number(values?.daysAwayFromWork) ===
                investigationData?.daysAwayFromWork
                  ? null
                  : values?.daysAwayFromWork,

              jobRestrictionDays:
                Number(values?.jobRestrictionDays) ===
                investigationData?.jobRestrictionDays
                  ? null
                  : values?.jobRestrictionDays,

              classification:
                values?.classification === investigationData?.classification
                  ? null
                  : values?.classification,

              dateOfDeath:
                values?.dateOfDeath === investigationData?.dateOfDeath
                  ? null
                  : moment(values?.dateOfDeath).isValid()
                    ? moment(values?.dateOfDeath).format('DD/MM/YYYY')
                    : null,
              treatmentFacilityType:
                values?.treatmentFacilityType ===
                investigationData?.treatmentFacilityType
                  ? null
                  : values?.treatmentFacilityType,
              narBeforeIncident:
                values?.narBeforeIncident ===
                investigationData?.narBeforeIncident
                  ? null
                  : values?.narBeforeIncident,
              narWhatHappened:
                values?.narWhatHappened === investigationData?.narWhatHappened
                  ? null
                  : values?.narWhatHappened,
              narObjectSubstance:
                values?.narObjectSubstance ===
                investigationData?.narObjectSubstance
                  ? null
                  : values?.narObjectSubstance,
              rootCauseAnalysis:
                values?.rootCauseAnalysis ===
                investigationData?.rootCauseAnalysis
                  ? null
                  : values?.rootCauseAnalysis,
            };
          } else {
            cleanedData = {
              type:
                values?.injuryIllness === investigationData?.type
                  ? null
                  : values?.injuryIllness,
              affectedBodyParts:
                values?.bodyPartAffected ===
                investigationData?.affectedBodyParts
                  ? null
                  : values?.bodyPartAffected,
              typeOfLoss:
                values?.typeOfLoss === investigationData?.typeOfLoss
                  ? null
                  : values?.typeOfLoss,
              classifyIllness:
                values?.classifyIllness === investigationData?.classifyIllness
                  ? null
                  : values?.classifyIllness,
              dateOfDeath:
                values?.dateOfDeath === investigationData?.dateOfDeath
                  ? null
                  : moment(values?.dateOfDeath).isValid()
                    ? moment(values?.dateOfDeath).format('DD/MM/YYYY')
                    : null,

              treatmentFacilityType:
                values?.treatmentFacilityType ===
                investigationData?.treatmentFacilityType
                  ? null
                  : values?.treatmentFacilityType,
              narBeforeIncident:
                values?.narBeforeIncident ===
                investigationData?.narBeforeIncident
                  ? null
                  : values?.narBeforeIncident,
              narWhatHappened:
                values?.narWhatHappened === investigationData?.narWhatHappened
                  ? null
                  : values?.narWhatHappened,
              narObjectSubstance:
                values?.narObjectSubstance ===
                investigationData?.narObjectSubstance
                  ? null
                  : values?.narObjectSubstance,
              daysAwayFromWork:
                Number(values?.daysAwayFromWork) ===
                investigationData?.daysAwayFromWork
                  ? null
                  : values?.daysAwayFromWork,
              jobRestrictionDays:
                Number(values?.jobRestrictionDays) ===
                investigationData?.jobRestrictionDays
                  ? null
                  : values?.jobRestrictionDays,
              classification:
                values?.classification === investigationData?.classification
                  ? null
                  : values?.classification,
              // step 2
              fiveWhys: isEqual(
                values?.fiveWhysQuestionAnswer,
                investigationData?.fiveWhys,
              )
                ? null
                : (values?.fiveWhysQuestionAnswer ?? []),

              // step 3
              customQuestions: isEqual(
                values?.customQuestions?.map((node: any) => ({
                  questionId: node?.questionId,
                  answer: node?.answer,
                })),
                flatMap?.map((node: any) => ({
                  questionId: node?.id,
                  answer: node?.answer,
                })),
              )
                ? null
                : values?.customQuestions || [],

              // step 4
              otherQuestions: isEqual(
                values?.otherQuestions,
                investigationData?.otherQuestionAnswer?.map((node) => ({
                  questionType: node?.questionType,
                  question: node?.question,
                  answer: node?.answer,
                })),
              )
                ? null
                : values?.otherQuestions,

              rootCauseAnalysis:
                values?.rootCauseAnalysis ===
                investigationData?.rootCauseAnalysis
                  ? null
                  : values?.rootCauseAnalysis,
            };
          }
        } else {
          if (values?.files?.length > 0 || attachments?.length > 0) {
            cleanedData = {
              type: values?.injuryIllness ?? '',
              affectedBodyParts: values?.bodyPartAffected ?? '',
              typeOfLoss: values?.typeOfLoss ?? '',
              classifyIllness: values?.classifyIllness ?? '',
              daysAwayFromWork: values?.daysAwayFromWork ?? '',
              jobRestrictionDays: values?.jobRestrictionDays ?? '',
              classification: values?.classification ?? '',
              dateOfDeath: values?.dateOfDeath
                ? moment(values?.dateOfDeath).isValid()
                  ? moment(values?.dateOfDeath).format('DD/MM/YYYY')
                  : null
                : null,
              treatmentFacilityType: values?.treatmentFacilityType ?? '',
              narBeforeIncident: values?.narBeforeIncident ?? '',
              narWhatHappened: values?.narWhatHappened ?? '',
              narObjectSubstance: values?.narObjectSubstance ?? '',
              // step 4
              rootCauseAnalysis: values?.rootCauseAnalysis ?? '',
            };
          } else {
            cleanedData = {
              type: values?.injuryIllness ?? '',
              affectedBodyParts: values?.bodyPartAffected ?? '',
              typeOfLoss: values?.typeOfLoss ?? '',
              classifyIllness: values?.classifyIllness ?? '',
              daysAwayFromWork: values?.daysAwayFromWork ?? '',
              jobRestrictionDays: values?.jobRestrictionDays ?? '',
              classification: values?.classification ?? '',
              dateOfDeath: values?.dateOfDeath
                ? moment(values?.dateOfDeath).isValid()
                  ? moment(values?.dateOfDeath).format('DD/MM/YYYY')
                  : null
                : null,
              treatmentFacilityType: values?.treatmentFacilityType ?? '',
              narBeforeIncident: values?.narBeforeIncident ?? '',
              narWhatHappened: values?.narWhatHappened ?? '',
              narObjectSubstance: values?.narObjectSubstance ?? '',
              // step 2
              fiveWhys: values?.fiveWhysQuestionAnswer ?? [],
              // step 3
              customQuestions: values?.customQuestions ?? [],
              // step 4
              otherQuestions: values?.otherQuestions ?? [],
              rootCauseAnalysis: values?.rootCauseAnalysis ?? '',
            };
          }
        }

        const removeEmptyValues: any = Object.fromEntries(
          Object.entries(cleanedData).filter(([, value]) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'boolean') return value;
            return value !== '' && value !== null && value !== undefined;
          }),
        );

        let res;
        if (investigationId) {
          res =
            await InvestigationReportingApi.investigationReportingControllerUpdateInvestigationReporting(
              {
                investigationId,
                attachmentIds: attachments?.map((node) => node?.id) || [],
                body: JSON.stringify(removeEmptyValues),
                files: values?.files?.length > 0 ? values?.files : [],
              },
            );
        } else {
          res =
            await InvestigationReportingApi.investigationReportingControllerCreateInvestigationReporting(
              {
                incidentId: id,
                body: JSON.stringify(removeEmptyValues),
                files: values?.files?.length > 0 ? values?.files : [],
              },
            );
        }
        toast.success(res?.data?.message);
        investigationReportingHandler?.resetForm();
        navigate(`/injuries/${res?.data?.investigation?.id}`);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const setAllTouched = (errors: any) => {
    const markTouched = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) {
        return true;
      }
      if (Array.isArray(obj)) {
        return obj.map((item) => markTouched(item));
      }
      return Object.keys(obj).reduce((acc: any, key) => {
        acc[key] = markTouched(obj[key]);
        return acc;
      }, {});
    };
    return markTouched(errors);
  };

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await investigationReportingHandler.validateForm();

      if (currentStep === 1) {
        if (Object.keys(errors).length > 0) {
          const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
            acc[key] = true;
            return acc;
          }, {});

          investigationReportingHandler.setTouched(touchedFields);
        } else {
          window.scrollTo(0, 0);
          setCurrentStep(steps);
        }
      } else if (currentStep === 2 || currentStep === 3 || currentStep === 4) {
        if (Object.keys(errors).length > 0) {
          investigationReportingHandler.setTouched(setAllTouched(errors));
        } else {
          window.scrollTo(0, 0);
          setCurrentStep(steps);
        }
      }
    }
  };

  useEffect(() => {
    const flatMap = finalCustomQuestions?.flatMap(
      (category: any) => category.questions,
    );

    if (investigationData) {
      investigationReportingHandler.setValues((prev: any) => ({
        ...prev,
        injuryIllness: investigationData?.type ?? '',
        bodyPartAffected: investigationData?.affectedBodyParts ?? [],
        typeOfLoss: investigationData?.typeOfLoss ?? '',
        classification: investigationData?.classification ?? '',
        classifyIllness: investigationData?.classifyIllness ?? '',
        daysAwayFromWork: investigationData?.daysAwayFromWork ?? '',
        jobRestrictionDays: investigationData?.jobRestrictionDays ?? '',
        dateOfDeath: investigationData?.dateOfDeath ?? '',
        treatmentFacilityType: investigationData?.treatmentFacilityType ?? '',
        narBeforeIncident: investigationData?.narBeforeIncident ?? '',
        narObjectSubstance: investigationData?.narObjectSubstance ?? '',
        narWhatHappened: investigationData?.narWhatHappened ?? '',
        files: [],
        fiveWhysQuestionAnswer:
          (investigationData?.fiveWhys || [])?.length > 0
            ? investigationData?.fiveWhys
            : Object.values(FiveWhysQuestionAnswerQuestionEnum).map(
                (question) => ({
                  question,
                  answer: '',
                }),
              ),
        customQuestions:
          (flatMap || [])?.length > 0
            ? flatMap?.map((node: any) => ({
                questionId: node?.id,
                answer: node?.answer,
              }))
            : [],
        rootCauseAnalysis: investigationData?.rootCauseAnalysis ?? '',
        otherQuestions:
          (investigationData?.otherQuestionAnswer || [])?.length > 0
            ? investigationData?.otherQuestionAnswer?.map((node) => ({
                questionType: node?.questionType,
                question: node?.question,
                answer: node?.answer,
              })) || []
            : [],
      }));
      if ((investigationData?.attachments || [])?.length > 0) {
        setAttachments(investigationData?.attachments || []);
      } else {
        setAttachments([]);
      }
    }
  }, [investigationData]);

  function areObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  }

  const isFormUnchanged = areObjectsEqual(
    {
      injuryIllness: investigationReportingHandler?.values?.injuryIllness,
      bodyPartAffected: JSON.stringify(
        investigationReportingHandler?.values?.bodyPartAffected,
      ),
      typeOfLoss: investigationReportingHandler?.values?.typeOfLoss,
      classification: investigationReportingHandler?.values?.classification,
      classifyIllness: investigationReportingHandler?.values?.classifyIllness,
      daysAwayFromWork: investigationReportingHandler?.values?.daysAwayFromWork,
      jobRestrictionDays:
        investigationReportingHandler?.values?.jobRestrictionDays,
      dateOfDeath: investigationReportingHandler?.values?.dateOfDeath,

      narBeforeIncident:
        investigationReportingHandler?.values?.narBeforeIncident,
      narWhatHappened: investigationReportingHandler?.values?.narWhatHappened,
      narObjectSubstance:
        investigationReportingHandler?.values?.narObjectSubstance,
      treatmentFacilityType:
        investigationReportingHandler?.values?.treatmentFacilityType,
      rootCauseAnalysis:
        investigationReportingHandler?.values?.rootCauseAnalysis,

      files: JSON.stringify(investigationReportingHandler?.values?.files || []),
      attachments: JSON.stringify(attachments),
      customQuestions: JSON.stringify(
        investigationReportingHandler?.values?.customQuestions,
      ),
      fiveWhysQuestionAnswer: JSON.stringify(
        investigationReportingHandler?.values?.fiveWhysQuestionAnswer,
      ),
      otherQuestions: JSON.stringify(
        investigationReportingHandler?.values?.otherQuestions,
      ),
    },
    {
      injuryIllness: investigationData?.type || '',
      bodyPartAffected:
        JSON.stringify(investigationData?.affectedBodyParts) || '',
      typeOfLoss: investigationData?.typeOfLoss || '',
      classification: investigationData?.classification || '',
      classifyIllness: investigationData?.classifyIllness || '',
      daysAwayFromWork: investigationData?.daysAwayFromWork || '',
      jobRestrictionDays: investigationData?.jobRestrictionDays || '',
      dateOfDeath: investigationData?.dateOfDeath || '',
      treatmentFacilityType: investigationData?.treatmentFacilityType || '',
      narBeforeIncident: investigationData?.narBeforeIncident || '',
      narObjectSubstance: investigationData?.narObjectSubstance || '',
      narWhatHappened: investigationData?.narWhatHappened || '',
      rootCauseAnalysis: investigationData?.rootCauseAnalysis || '',

      files: JSON.stringify(
        investigationReportingHandler?.initialValues?.files || [],
      ),
      attachments: JSON.stringify(investigationData?.attachments),
      customQuestions: JSON.stringify(
        (
          finalCustomQuestions?.flatMap(
            (category: any) => category.questions,
          ) || []
        )?.length > 0
          ? finalCustomQuestions
              ?.flatMap((category: any) => category.questions)
              ?.map((node: any) => ({
                questionId: node?.id,
                answer: node?.answer,
              }))
          : [],
      ),
      fiveWhysQuestionAnswer: JSON.stringify(
        (investigationData?.fiveWhys || [])?.length > 0 &&
          investigationData?.fiveWhys,
      ),

      otherQuestions: JSON.stringify(
        investigationData?.otherQuestionAnswer?.map((node) => ({
          questionType: node?.questionType,
          question: node?.question,
          answer: node?.answer,
        })) || [],
      ),
    },
  );

  const steps: Record<number, JSX.Element> = {
    1: (
      <InvestigationReportingStepOne
        data={data}
        formHandler={investigationReportingHandler}
        handleNextStep={handleNextStep}
        attachments={attachments || []}
        questions={
          investigationId ? finalCustomQuestions : investigationQuestions
        }
        handleChangeAttachments={(e) => setAttachments(e)}
      />
    ),
    2: (
      <InvestigationReportingStepTwo
        formHandler={investigationReportingHandler}
        handleNextStep={handleNextStep}
        attachments={attachments || []}
        questions={
          investigationId ? finalCustomQuestions : investigationQuestions
        }
      />
    ),
    3: (
      <InvestigationReportingStepThree
        formHandler={investigationReportingHandler}
        handleNextStep={handleNextStep}
        questions={
          investigationId ? finalCustomQuestions : investigationQuestions
        }
      />
    ),
    4: (
      <InvestigationReportingStepFour
        formHandler={investigationReportingHandler}
        handleNextStep={handleNextStep}
        questions={
          investigationId ? finalCustomQuestions : investigationQuestions
        }
        attachments={attachments || []}
      />
    ),
    5: (
      <InvestigationReportingStepFive
        formHandler={investigationReportingHandler}
        handleNextStep={handleNextStep}
        questions={
          investigationId ? finalCustomQuestions : investigationQuestions
        }
        attachments={attachments || []}
        disbaleButtons={isFormUnchanged}
      />
    ),
  };

  if (isLoading || isInvestigationQuestionsLoading || isInvestigationLoading) {
    return <Loader />;
  }

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form action="#" onSubmit={investigationReportingHandler.handleSubmit}>
        {steps[currentStep]}
      </form>
    </div>
  );
}
